import {downloadFile, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import React, {ForwardedRef, forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ActivateHandle} from '../../../util/interfaces';
import {exportProductCostsV2, exportVATs, importProductCostsV2, importVats} from '../../../util/requestSender';
import {createErrorMessage, downloadFileURL} from '../../../util/utils';
import {AppContext} from '../../misc/appContext';
import ImportExportDialog, {ImportErrorState, ImportExportMode} from './importExportDialog';

interface Props {
    onProductsChanged: VoidFunction;
    defaultMode?: ImportExportMode;
    title?: string;
    modesHidden?: boolean;
}

const VAT_TEMPLATE_URL = 'https://app.bluemate.nl/static/btw-import-template-v2.xlsx';
const COSTS_TEMPLATE_URL = 'https://app.bluemate.nl/static/product-kosten-v2-template-{currentLanguage}.xlsx';

function ImportExportDialogController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {onProductsChanged, defaultMode = 'vat', title, modesHidden} = props;
    const {account} = useContext(AppContext).accountContext;
    const {t, i18n} = useTranslation();
    const [active, setActive] = useState(false);
    const [mode, setMode] = useState<ImportExportMode>(defaultMode);
    const [exporting, setExporting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importFile, setImportFile] = useState<File | null>(null);
    const [applyRetroactive, setApplyRetroactive] = useState(false);
    const [importError, setImportError] = useState<ImportErrorState>(null);
    const errorFile = useRef<string | null>(null);
    const modeRef = useRef<ImportExportMode>(defaultMode);

    function onActivate() {
        modeRef.current = defaultMode;
        setMode(defaultMode);
        setImportFile(null);
        setApplyRetroactive(false);
        clearImportError();
        setActive(true);
    }

    function onClose() {
        if (exporting || importing) return;
        setActive(false);
        setImportFile(null);
        clearImportError();
    }

    function onChangeMode(value: ImportExportMode) {
        modeRef.current = value;
        setMode(value);
        setExporting(false);
        setImporting(false);
        setImportFile(null);
        setApplyRetroactive(false);
        clearImportError();
    }

    function onSelectFile(file: File | null) {
        if (!file) return;
        setImportFile(file);
        clearImportError();
    }

    function onChangeApplyRetroactive() {
        setApplyRetroactive(current => !current);
    }

    function onDownloadTemplate() {
        if (mode === 'vat') downloadFileURL(VAT_TEMPLATE_URL, 'bluemate-btw-import-template.xlsx');
        else {
            let currentLanguage = i18n.language;
            // Fallback to nl for unexpected languages
            if (!['nl', 'en'].includes(currentLanguage)) currentLanguage = 'nl';
            const fileURL = COSTS_TEMPLATE_URL.replace('{currentLanguage}', currentLanguage);
            downloadFileURL(fileURL, 'product-kosten-import-template.xlsx');
        }
    }

    function onDownloadErrorFile() {
        if (!errorFile.current) return;
        const link = document.createElement('a');
        link.href = errorFile.current;
        link.download = 'import-fouten.xlsx';
        link.click();
    }

    function onSubmitImport() {
        if (!account || !importFile || importing) return;
        if (!importFile.name.toLowerCase().endsWith('.xlsx'))
            return showDialog(
                t(
                    'products.importExport.onlyXlsxAccepted',
                    'We accepteren alleen .xlsx bestanden, gebruik het voorbeeldbestand.'
                )
            );
        setImporting(true);
        if (mode === 'vat') onSubmitVAT();
        else if (mode === 'cost') onSubmitProductCosts();
    }

    function onSubmitVAT() {
        importVats({
            file: importFile!,
            account_id: account!.id,
            create_non_existing: false,
            apply_retroactive: applyRetroactive,
            callback: onImportResponse(mode),
            errorCallback: importFailed(mode)
        });
    }

    function onSubmitProductCosts() {
        importProductCostsV2({
            file: importFile!,
            account_id: account!.id,
            callback: onImportResponse(mode),
            errorCallback: importFailed(mode)
        });
    }

    function importFailed(submittedMode: ImportExportMode) {
        return function () {
            if (modeRef.current !== submittedMode) return;
            setImporting(false);
            showDialog(
                createErrorMessage(t('products.importExport.importFailedMsg', 'Het importeren is niet gelukt.')),
                t('products.importExport.importFailedTitle', 'Importeren mislukt')
            );
        };
    }

    function onImportResponse(submittedMode: ImportExportMode) {
        return function (data: ResponseData, response: Response) {
            if (modeRef.current !== submittedMode) return;
            handleImportResponse(data, response);
        };
    }

    function handleImportResponse(data: ResponseData, response: Response) {
        setImporting(false);
        if (response.headers.get('Content-Type') === 'application/json') {
            if (data.success) {
                const {num_imported, row_errors} = data;
                const hasRowErrors = !!row_errors && Object.keys(row_errors).length > 0;
                if (hasRowErrors) setImportError({kind: 'rows', rows: row_errors});
                const countLabel =
                    num_imported === 1
                        ? t('products.importExport.row', 'regel')
                        : t('products.importExport.rows', 'regels');
                onProductsChanged();
                if (!hasRowErrors) onClose();
                if (num_imported)
                    showSnackbar(
                        t('products.importExport.importSuccess', 'Succesvol {{num}} {{label}} bijgewerkt', {
                            num: num_imported,
                            label: countLabel
                        })
                    );
            } else
                showDialog(
                    createErrorMessage(
                        data.message ?? t('products.importExport.importFailedMsg', 'Het importeren is niet gelukt.')
                    ),
                    t('products.importExport.importFailedTitle', 'Importeren mislukt')
                );
        } else if (data.status === 'success') {
            if (mode === 'vat') onVATImportSuccess(response);
        }
    }

    function onVATImportSuccess(response: Response) {
        void response
            .blob()
            .then((blob: Blob) => {
                const numError = Number(response.headers.get('X-Num-Error') ?? 0);
                const numImported = Number(response.headers.get('X-Num-Imported') ?? 0);
                if (errorFile.current) URL.revokeObjectURL(errorFile.current);
                errorFile.current = URL.createObjectURL(blob);
                onProductsChanged();
                setImportError({
                    kind: 'text',
                    text: t(
                        'products.importExport.partialImportError',
                        '{{imported}} regels zijn geïmporteerd, maar {{errors}} kon{{suffix}} niet geïmporteerd worden. Zie het bestand hieronder voor de details.',
                        {imported: numImported, errors: numError, suffix: numError === 1 ? '' : 'den'}
                    )
                });
            })
            .catch(importFailed(modeRef.current));
    }

    function onClickExport() {
        if (!account || exporting) return;
        setExporting(true);
        if (mode === 'vat') onExportVAT();
        else if (mode === 'cost') onExportProductCosts();
    }

    function onExportVAT() {
        exportVATs({
            account_id: account!.id,
            callback: exportCallback('btw-tarieven-export.xlsx', vatExportFailed),
            errorCallback: vatExportFailed
        });
    }

    function vatExportFailed() {
        setExporting(false);
        showDialog(
            createErrorMessage(t('products.importExport.exportFailed', 'Het exporteren is niet gelukt.')),
            t('products.importExport.exportFailedTitle', 'Exporteren mislukt')
        );
    }

    function onExportProductCosts() {
        exportProductCostsV2({
            account_id: account!.id,
            callback: exportCallback('product-kosten-export.xlsx', costExportFailed),
            errorCallback: costExportFailed
        });
    }

    function costExportFailed() {
        setExporting(false);
        showDialog(
            createErrorMessage(t('products.importExport.exportFailed', 'Het exporteren is niet gelukt.')),
            t('products.importExport.exportFailedTitle', 'Exporteren mislukt')
        );
    }

    function exportCallback(fileName: string, failureHandler: VoidFunction) {
        return function (data: ResponseData, response: Response) {
            if (data.status === 'success')
                response
                    .blob()
                    .then((blob: Blob) => {
                        const header = response.headers.get('Content-Disposition');
                        if (header)
                            fileName = header.substring(
                                header.indexOf('filename=') + 'filename='.length,
                                header.length
                            );
                        downloadFile(blob, fileName);
                        setExporting(false);
                    })
                    .catch(failureHandler);
            else {
                setExporting(false);
                showDialog(
                    createErrorMessage(
                        data.message ?? t('products.importExport.exportFailed', 'Het exporteren is niet gelukt.')
                    ),
                    t('products.importExport.exportFailedTitle', 'Exporteren mislukt')
                );
            }
        };
    }

    function clearImportError() {
        setImportError(null);
        if (errorFile.current) URL.revokeObjectURL(errorFile.current);
        errorFile.current = null;
    }

    useEffect(() => {
        return () => {
            if (errorFile.current) URL.revokeObjectURL(errorFile.current);
        };
    }, []);

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <ImportExportDialog
            onClose={onClose}
            onChangeMode={onChangeMode}
            onClickExport={onClickExport}
            onSelectFile={onSelectFile}
            onChangeApplyRetroactive={onChangeApplyRetroactive}
            onSubmitImport={onSubmitImport}
            onDownloadTemplate={onDownloadTemplate}
            onDownloadErrorFile={onDownloadErrorFile}
            active={active}
            mode={mode}
            exporting={exporting}
            importing={importing}
            importFile={importFile}
            applyRetroactive={applyRetroactive}
            importError={importError}
            title={title}
            modesHidden={modesHidden}
        />
    );
}

export default forwardRef<ActivateHandle, Props>(ImportExportDialogController);
